import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, CATEGORIES, CATEGORY_NAME } from "@/lib/api";
import { Building2, ChevronLeft, Calendar, Film, Users, UserPlus, X, Loader2, Trash2, Pencil, ExternalLink, Plus, ArrowRightLeft } from "lucide-react";
import DeleteButton from "@/components/DeleteButton";
import RenameModal from "@/components/RenameModal";
import AutocompleteInput from "@/components/AutocompleteInput";

/**
 * `/associations/:name` — detail page.
 * Left column: events under this association (click → jump to /events/:name).
 * Right column: admin roster + Add / Remove admins.
 */
export default function AssociationDetail() {
  const { name: rawName } = useParams();
  const name = decodeURIComponent(rawName || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    return api.get(`/associations/${encodeURIComponent(name)}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [name]);

  const removeAdmin = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email} as admin of ${name}?`)) return;
    try {
      await api.delete(`/associations/${encodeURIComponent(name)}/admins/${u.id}`);
      toast.success("Admin removed");
      load();
    } catch (err) {
      toast.error("Remove failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8">
        <Link to="/associations" className="btn-secondary inline-flex items-center gap-1.5 mb-4" data-testid="back-to-associations">
          <ChevronLeft size={13} /> Back to Associations
        </Link>
        <div className="border border-dashed border-stone-700 p-8 text-center text-stone-500">
          Association not found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl">
      <Link to="/associations" className="btn-secondary inline-flex items-center gap-1.5 mb-4" data-testid="back-to-associations">
        <ChevronLeft size={13} /> Back to Associations
      </Link>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div className="min-w-0 flex-1">
          <div className="label-tiny mb-2">Association</div>
          <div className="flex items-center gap-2">
            <Building2 size={30} className="text-orange-500 shrink-0" />
            <h1 className="font-display uppercase text-5xl font-bold leading-[0.95] break-words">
              {data.name}
            </h1>
            <button
              type="button"
              onClick={() => setRenaming(true)}
              className="p-2 border border-stone-700 hover:border-orange-500 text-stone-400 hover:text-orange-400 transition-colors ml-2"
              title="Rename association (cascades across every video, clip and admin)"
              data-testid="association-rename-btn"
            >
              <Pencil size={13} />
            </button>
          </div>
          <div className="text-xs text-stone-400 mt-3 flex flex-wrap gap-x-3 gap-y-1 font-mono-tc">
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> {data.event_count} events</span>
            <span className="inline-flex items-center gap-1"><Film size={11} /> {data.clip_count} clips</span>
            <span className="inline-flex items-center gap-1"><Users size={11} /> {(data.admins || []).length} admin{((data.admins || []).length) === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6">
        {/* LEFT — Performances (nested: performance → events/disciplines) */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display uppercase text-lg">Performances under this association</div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-stone-500">
                {(data.performances || []).length}
              </span>
              <button
                onClick={() => setShowNewEvent(true)}
                className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1 hover:!text-orange-400"
                data-testid="new-performance-btn"
              >
                <Plus size={11} /> New Performance
              </button>
            </div>
          </div>
          {(data.performances || []).length === 0 ? (
            <div className="border border-dashed border-stone-700 p-6 text-center text-stone-500 text-sm">
              No performances tagged with this association yet. Upload a video, tag it with this
              association, and set its performance name to see it here.
            </div>
          ) : (
            <div className="space-y-3" data-testid="association-performances-list">
              {(data.performances || []).map((p) => (
                <div
                  key={p.name}
                  className="border border-stone-800 hover:border-stone-700 transition-colors group"
                  data-testid={`association-performance-row-${p.name.replace(/\s+/g, "_")}`}
                >
                  {/* Performance header */}
                  <div className="flex items-center border-b border-stone-800/60">
                    <button
                      onClick={() => navigate(`/performances/${encodeURIComponent(p.name)}`)}
                      className="flex-1 text-left px-4 py-3 flex items-center gap-3 hover:bg-stone-900/60"
                    >
                      <Calendar size={14} className="text-orange-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-display uppercase text-sm font-semibold truncate">
                          {p.name}
                        </div>
                        <div className="text-[11px] text-stone-500 font-mono-tc">
                          {p.clip_count} clip{p.clip_count === 1 ? "" : "s"} · {p.events.length} event{p.events.length === 1 ? "" : "s"}
                          {!p.explicit && <span className="ml-2 text-stone-600 italic">(auto-grouped)</span>}
                        </div>
                      </div>
                      <ExternalLink size={12} className="text-stone-600" />
                    </button>
                    <div className="pr-3">
                      <DeleteButton
                        testId={`delete-performance-${p.name.replace(/\s+/g, "_")}`}
                        label={p.name}
                        hoverOnly
                        onDelete={async () => {
                          const r = await api.delete(`/performances/${encodeURIComponent(p.name)}`);
                          load();
                          return r;
                        }}
                        successMessage={`"${p.name}" removed — ${p.clip_count} clip${p.clip_count === 1 ? "" : "s"} untagged`}
                      />
                    </div>
                  </div>
                  {/* Events (disciplines) inside this performance */}
                  <div className="px-4 py-2 flex flex-wrap gap-1.5">
                    {p.events.map((e) => (
                      <EventChip
                        key={e.category}
                        chip={e}
                        performance={p}
                        association={data.name}
                        allPerformances={(data.performances || []).map((x) => x.name)}
                        onChanged={load}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — Admin roster */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="font-display uppercase text-lg inline-flex items-center gap-1.5">
              <Users size={14} className="text-orange-500" /> Managed by
            </div>
            <button
              onClick={() => setShowAddAdmin(true)}
              className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1"
              data-testid="add-admin-btn"
            >
              <UserPlus size={11} /> Add admin
            </button>
          </div>
          {(data.admins || []).length === 0 ? (
            <div className="border border-dashed border-stone-700 p-4 text-center text-stone-500 text-sm">
              No admins yet.
            </div>
          ) : (
            <div className="border border-stone-800 divide-y divide-stone-800" data-testid="association-admins-list">
              {data.admins.map((u) => (
                <div key={u.id} className="px-3 py-2 flex items-center gap-2" data-testid={`association-admin-row-${u.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-stone-200 truncate">{u.name || "(no name)"}</div>
                    <div className="text-[11px] text-stone-500 truncate">{u.email}</div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${u.role === "admin" ? "border-orange-700 text-orange-400" : "border-stone-700 text-stone-400"}`}>
                    {u.role}
                  </span>
                  <button
                    onClick={() => removeAdmin(u)}
                    className="text-stone-500 hover:text-rose-400 p-1"
                    title="Remove admin"
                    data-testid={`association-admin-remove-${u.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAddAdmin && (
        <AddAssociationAdminModal
          associationName={name}
          existingUserIds={new Set((data.admins || []).map((u) => u.id))}
          onClose={() => setShowAddAdmin(false)}
          onAdded={() => { setShowAddAdmin(false); load(); }}
        />
      )}

      {showNewEvent && (
        <NewEventModal
          associationName={name}
          onClose={() => setShowNewEvent(false)}
          onCreated={() => { setShowNewEvent(false); load(); }}
        />
      )}

      {renaming && (
        <RenameModal
          label="Rename Association"
          currentName={data.name}
          subtitle="Cascades across every video, clip and admin who currently references this association."
          testIdPrefix="association-rename"
          maxLen={120}
          onSave={async (newName) => {
            const r = await api.put(`/associations/${encodeURIComponent(data.name)}/rename`, { new_name: newName });
            const bits = [];
            if (r.data?.videos_renamed) bits.push(`${r.data.videos_renamed} videos`);
            if (r.data?.clips_renamed) bits.push(`${r.data.clips_renamed} clips`);
            if (r.data?.users_updated) bits.push(`${r.data.users_updated} admins`);
            toast.success(`Renamed${bits.length ? " · " + bits.join(", ") : ""}`);
            navigate(`/associations/${encodeURIComponent(newName)}`, { replace: true });
          }}
          onClose={() => setRenaming(false)}
        />
      )}
    </div>
  );
}

function AddAssociationAdminModal({ associationName, existingUserIds, onClose, onAdded }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/auth/users")
      .then((r) => setUsers((r.data.users || []).filter((u) => !existingUserIds.has(u.id))))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [existingUserIds]);

  const add = async (u) => {
    setSaving(u.id);
    try {
      await api.post(`/associations/${encodeURIComponent(associationName)}/admins`, { user_id: u.id });
      toast.success(`Added ${u.name || u.email}`);
      onAdded();
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(null);
    }
  };

  const filtered = q.trim()
    ? users.filter((u) =>
        (u.name || "").toLowerCase().includes(q.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(q.toLowerCase())
      )
    : users;

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-admin-modal"
    >
      <div className="bg-stone-950 border border-orange-700 max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div>
            <div className="label-tiny">Add admin to</div>
            <div className="font-display uppercase text-lg leading-tight break-words">{associationName}</div>
          </div>
          <button onClick={onClose} className="btn-secondary" data-testid="add-admin-close"><X size={13} /></button>
        </div>
        <div className="p-3 border-b border-stone-800">
          <input
            autoFocus
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="field-input w-full"
            data-testid="add-admin-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto min-h-[200px]">
          {loading ? (
            <div className="p-4 text-xs text-stone-500 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Loading users…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-xs text-stone-500 text-center">
              {q.trim() ? "No users match." : "Every user is already an admin here — create a new account first."}
            </div>
          ) : (
            filtered.map((u) => (
              <button
                key={u.id}
                onClick={() => add(u)}
                disabled={saving === u.id}
                className="w-full text-left px-4 py-2 hover:bg-stone-900 flex items-center gap-2 border-b border-stone-800/70 disabled:opacity-50"
                data-testid={`add-admin-user-${u.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-stone-200 truncate">{u.name || "(no name)"}</div>
                  <div className="text-[11px] text-stone-500 truncate">{u.email}</div>
                </div>
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${u.role === "admin" ? "border-orange-700 text-orange-400" : "border-stone-700 text-stone-400"}`}>
                  {u.role}
                </span>
                {saving === u.id ? <Loader2 size={12} className="animate-spin text-orange-400" /> : <UserPlus size={12} className="text-orange-500" />}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


function NewEventModal({ associationName, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/associations/${encodeURIComponent(associationName)}/events`, {
        name: name.trim(),
        event_date: eventDate,
        category,
        notes: notes.trim(),
      });
      toast.success(`Performance "${name.trim()}" created`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create performance");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-performance-modal">
      <div className="bg-stone-950 border border-orange-700 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <div className="label-tiny">New performance under</div>
            <div className="font-display uppercase text-lg leading-tight break-words">{associationName}</div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-tiny block mb-1">Performance name</label>
            <AutocompleteInput
              value={name}
              onChange={setName}
              field="event_name"
              placeholder="e.g. Chinook Rodeo Finals — Perf #3"
              className="field-input w-full"
              data-testid="new-performance-name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-tiny block mb-1">Date (optional)</label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="field-input w-full"
                data-testid="new-event-date"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1">Category (optional)</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="field-input w-full"
                data-testid="new-event-category"
              >
                <option value="">—</option>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label-tiny block mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="field-input w-full resize-none"
              placeholder="Internal notes — location, contact, etc."
              data-testid="new-event-notes"
            />
          </div>
          <div className="text-[11px] text-stone-500">
            The performance will appear immediately under this association. Uploading a matching video later
            auto-links to it.
          </div>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="btn-primary w-full inline-flex items-center justify-center gap-1.5"
            data-testid="new-performance-submit"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            {busy ? "Creating…" : "Create Performance"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Event chip inside a performance card. Shows discipline label + clip
// count. On hover reveals two icon actions:
//   ↔  Move — reassigns every clip in this discipline+performance to
//              another performance (via POST /performances/{from}/move-category)
//   ✎  Edit — jumps into the Library filtered to this discipline within
//              this association so the studio owner can bulk-adjust
// ─────────────────────────────────────────────────────────────────────
function EventChip({ chip, performance, association, allPerformances, onChanged }) {
  const [mode, setMode] = useState("idle"); // idle | move | busy
  const [selectedTarget, setSelectedTarget] = useState("");
  const navigate = useNavigate();

  const label = CATEGORY_NAME[chip.category] || chip.category;
  const testId = `perf-event-chip-${performance.name.replace(/\s+/g, "_")}-${chip.category}`;

  const openLibrary = (e) => {
    e.stopPropagation();
    navigate(`/library?category=${encodeURIComponent(chip.category)}&performance=${encodeURIComponent(performance.name)}`);
  };

  const move = async (target) => {
    if (!target || target === performance.name) return;
    setMode("busy");
    try {
      const r = await api.post(
        `/performances/${encodeURIComponent(performance.name)}/move-category`,
        { category: chip.category, to_performance: target },
      );
      toast.success(
        `Moved ${chip.clip_count} ${label} clip${chip.clip_count === 1 ? "" : "s"} to "${target}"${r.data?.videos_moved ? ` · ${r.data.videos_moved} video${r.data.videos_moved === 1 ? "" : "s"} retagged` : ""}`,
      );
      setMode("idle");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
      setMode("idle");
    }
  };

  const otherPerformances = allPerformances.filter((n) => n && n !== performance.name);

  if (mode === "move") {
    return (
      <div
        className="inline-flex items-center gap-1 px-2 py-1 border border-orange-700 bg-orange-950/30 text-[10px] uppercase tracking-widest"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testId}-move-bar`}
      >
        <ArrowRightLeft size={10} className="text-orange-400 shrink-0" />
        <span className="text-stone-400">Move to:</span>
        <select
          autoFocus
          value={selectedTarget}
          onChange={(e) => { setSelectedTarget(e.target.value); if (e.target.value) move(e.target.value); }}
          className="bg-stone-900 border border-stone-700 text-stone-200 text-[10px] px-1 py-0.5 max-w-[180px]"
          data-testid={`${testId}-move-select`}
        >
          <option value="">— pick performance —</option>
          {otherPerformances.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => { setMode("idle"); setSelectedTarget(""); }}
          className="text-stone-500 hover:text-stone-200 px-1"
          data-testid={`${testId}-move-cancel`}
          aria-label="Cancel move"
        >
          <X size={10} />
        </button>
      </div>
    );
  }

  return (
    <div
      className="group/chip inline-flex items-center border border-stone-800 hover:border-orange-700 text-[10px] uppercase tracking-widest text-stone-300 transition-colors"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={openLibrary}
        className="inline-flex items-center gap-1.5 px-2 py-1 hover:text-orange-400"
        title={`Open in Library · ${chip.clip_count} clip${chip.clip_count === 1 ? "" : "s"}`}
      >
        <span>{label}</span>
        <span className="text-stone-500 font-mono-tc">{chip.clip_count}</span>
      </button>
      <div className="flex items-center border-l border-stone-800">
        {otherPerformances.length > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMode("move"); }}
            disabled={mode === "busy"}
            className="p-1.5 text-stone-500 hover:text-orange-400 border-r border-stone-800 disabled:opacity-50"
            title={`Move ${label} clips to another performance`}
            data-testid={`${testId}-move-btn`}
            aria-label="Move to another performance"
          >
            {mode === "busy" ? <Loader2 size={10} className="animate-spin" /> : <ArrowRightLeft size={10} />}
          </button>
        )}
        <button
          type="button"
          onClick={openLibrary}
          className="p-1.5 text-stone-500 hover:text-orange-400"
          title={`Edit ${label} clips (opens Library filtered)`}
          data-testid={`${testId}-edit-btn`}
          aria-label="Edit clips"
        >
          <Pencil size={10} />
        </button>
      </div>
    </div>
  );
}

